import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
// 1. 补全需要的antd组件（适配交互+样式）
import { Card, Popconfirm, Button, message, Typography, Space } from 'antd';
import { DeleteOutlined, LeftOutlined } from '@ant-design/icons';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import './index.css';

const { Title, Paragraph } = Typography;

// 2. 接收全局传递的props（posts数据源、登录用户、删除方法）
const PostDetail = ({ posts = [], loggedInUser, onDeletePost }) => {
  const { id } = useParams();
  // 核心修复：不再强制转数字，使用字符串ID（和Home/Archive保持一致）
  const postId = id; 
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  // 加载文章详情（适配全局posts + 兼容mockApi）
  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        // 优先从全局posts获取（适配本地状态），兼容mockApi
        let targetPost = posts.find(item => String(item.id) === postId);
        if (!targetPost && window.mockPostsApi?.posts?.getPostById) {
          // 兼容原mockApi逻辑（如果需要）
          const response = await window.mockPostsApi.posts.getPostById(postId);
          if (response.success) {
            targetPost = response.data.post;
          } else {
            message.error('文章不存在或已被删除');
          }
        }
        setPost(targetPost);
      } catch (err) {
        console.error('加载文章失败：', err);
        message.error('加载文章失败，请稍后重试');
      } finally {
        setLoading(false);
      }
    };

    // 验证ID有效性：只需检查非空，不再校验是否为数字
    if (postId) {
      fetchPost();
    } else {
      setLoading(false);
      message.error('无效的文章ID');
    }
  }, [postId, posts]); // 依赖全局posts，数据更新时重新加载

  // 删除文章（补充权限控制 + 同步全局数据）
  const handleDelete = async () => {
    try {
      // 1. 权限控制：仅登录用户且是文章作者可删除
      if (!loggedInUser) {
        message.warning('请先登录后再删除文章');
        navigate('/auth');
        return;
      }
      if (String(post.authorId) !== String(loggedInUser.id)) {
        message.error('你没有权限删除该文章');
        return;
      }

      // 2. 优先调用全局删除方法（同步Home/Archive数据），兼容mockApi
      if (onDeletePost) {
        onDeletePost(postId); // 同步更新全局posts
        message.success('文章已成功删除');
        navigate('/archive');
      } else if (window.mockPostsApi?.posts?.deletePost) {
        const response = await window.mockPostsApi.posts.deletePost(postId);
        if (response.success) {
          message.success('文章已成功删除');
          navigate('/archive');
        } else {
          message.error('删除失败：' + (response.message || '未知错误'));
        }
      }
    } catch (err) {
      console.error('删除文章出错：', err);
      message.error('删除操作失败，请重试');
    }
  };

  if (loading) {
    return (
      <div className="post-detail-loading">
        <Header />
        <div className="loading-spinner" style={{ padding: '100px 0', textAlign: 'center', fontSize: '18px' }}>加载中...</div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="post-not-found">
        <Header />
        <div className="post-error-container" style={{ padding: '50px', textAlign: 'center' }}>
          <Title level={3}>文章未找到</Title>
          <Paragraph>该文章可能已被删除或不存在</Paragraph>
          <Space>
            <Button type="primary" onClick={() => navigate(-1)}>返回上一页</Button>
            <Button component={Link} to="/archive">返回归档页</Button>
          </Space>
        </div>
        <Footer />
      </div>
    );
  }

  // 仅作者可见删除按钮
  const canDelete = loggedInUser && String(post.authorId) === String(loggedInUser.id);

  return (
    <div className="post-detail-page">
      <Header />
      <div className="post-detail-container" style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
        {/* 新增返回按钮，提升交互体验 */}
        <Button 
          icon={<LeftOutlined />} 
          onClick={() => navigate(-1)} 
          style={{ marginBottom: '20px' }}
        >
          返回上一页
        </Button>

        <Card bordered={false} className="post-detail-card" style={{ padding: '24px' }}>
          <Title level={2}>{post.title}</Title>
          
          <div className="post-meta" style={{ display: 'flex', gap: '20px', margin: '16px 0', flexWrap: 'wrap' }}>
            <span>发布时间：{new Date(post.createTime).toLocaleDateString()}</span>
            {post.updateTime && (
              <span>更新时间：{new Date(post.updateTime).toLocaleDateString()}</span>
            )}
            <span>分类：{post.tags?.[0] || '未分类'}</span>
            
            {/* 仅作者可见删除按钮 */}
            {canDelete && (
              <Popconfirm
                title="确定要删除这篇文章吗？"
                description="删除后将无法恢复，请谨慎操作"
                onConfirm={handleDelete}
                okText="确认删除"
                cancelText="取消"
                placement="top"
              >
                <Button 
                  type="text" 
                  danger 
                  icon={<DeleteOutlined />}
                  className="post-delete-btn"
                >
                  删除文章
                </Button>
              </Popconfirm>
            )}
          </div>

          <Paragraph 
            className="post-content" 
            style={{ whiteSpace: 'pre-wrap', marginTop: 20, lineHeight: '1.8', fontSize: '16px' }}
          >
            {post.content}
          </Paragraph>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default PostDetail;